import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { PaymentItemSnapshot } from './billing-hooks'

export interface InvoiceRequestOrder {
  id: string
  order_number: string
  customer_name: string
  customer_phone: string | null
  total: number
  created_at: string
  invoice_customer_name: string | null
  invoice_legal_id: string | null
  invoice_email: string | null
  invoice_sent_at: string | null
  invoice_payment_id: string | null
  source: string | null
  delivery_type: string | null
}

export function useInvoiceRequests(restaurantId: string | number | undefined) {
  const [pendingRequests, setPendingRequests] = useState<InvoiceRequestOrder[]>(
    [],
  )
  const [sentRequests, setSentRequests] = useState<InvoiceRequestOrder[]>([])
  const [loading, setLoading] = useState(true)

  const fetchRequests = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    try {
      // Fetch all orders that requested an invoice and are not cancelled
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          id, order_number, customer_name, customer_phone, total, created_at,
          invoice_customer_name, invoice_legal_id, invoice_email, invoice_sent_at, invoice_payment_id,
          source, delivery_type
        `,
        )
        .eq('restaurant_id', restaurantId)
        .eq('wants_invoice', true)
        .not('status', 'eq', 'cancelled')
        .order('created_at', { ascending: false })

      if (error) throw error

      const pending: InvoiceRequestOrder[] = []
      const sent: InvoiceRequestOrder[] = []

      // Split into pending and sent (only sent today for the sent list to avoid huge lists)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      data?.forEach((order: any) => {
        if (!order.invoice_sent_at) {
          pending.push(order)
        } else {
          const sentDate = new Date(order.invoice_sent_at)
          if (sentDate >= today) {
            sent.push(order)
          }
        }
      })

      setPendingRequests(pending)
      setSentRequests(sent)
    } catch (err) {
      console.error('[NOIZA] Error fetching invoice requests:', err)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  return { pendingRequests, sentRequests, loading, refetch: fetchRequests }
}

// ─── NEW: Hook for full order history (all non-cancelled orders) ─────────
// Used by InvoiceRequestsPage "Historial" tab so the user can print or
// email an invoice for ANY order, not just the ones flagged wants_invoice.
export function useOrderHistory(
  restaurantId: string | number | undefined,
  limit: number = 200,
) {
  const [orders, setOrders] = useState<InvoiceRequestOrder[]>([])
  const [loading, setLoading] = useState(true)

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(
          `
          id, order_number, customer_name, customer_phone, total, created_at,
          invoice_customer_name, invoice_legal_id, invoice_email, invoice_sent_at, invoice_payment_id,
          source, delivery_type
        `,
        )
        .eq('restaurant_id', restaurantId)
        .not('status', 'eq', 'cancelled')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      setOrders((data || []) as InvoiceRequestOrder[])
    } catch (err) {
      console.error('[NOIZA] Error fetching order history:', err)
    } finally {
      setLoading(false)
    }
  }, [restaurantId, limit])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return { orders, loading, refetch: fetchOrders }
}

export async function getOrderItemsSnapshot(
  orderId: string,
): Promise<PaymentItemSnapshot[]> {
  try {
    const { data, error } = await supabase
      .from('order_items')
      .select(
        'id, item_name_snapshot, quantity, unit_price_snapshot, line_total, notes',
      )
      .eq('order_id', orderId)

    if (error) throw error

    return (data || []).map((item: any) => ({
      order_item_id: item.id,
      item_name: item.item_name_snapshot,
      quantity: item.quantity,
      unit_price: item.unit_price_snapshot,
      line_total: item.line_total,
      notes: item.notes,
    }))
  } catch (err) {
    console.error('[NOIZA] Error fetching order items for invoice:', err)
    return []
  }
}

export async function markInvoiceAsSent(orderId: string, paymentId: string) {
  try {
    const { error } = await supabase
      .from('orders')
      .update({
        invoice_sent_at: new Date().toISOString(),
        invoice_payment_id: paymentId,
      })
      .eq('id', orderId)

    if (error) throw error
    return { success: true }
  } catch (err: any) {
    console.error('[NOIZA] Error marking invoice as sent:', err)
    return { success: false, error: err.message }
  }
}
